import { monthShortNameFromIndex } from "@utils";
import moment from "moment";

export const arrayAverage = (array) =>
  array.reduce((a, b) => a + b) / array.length;

export const comparePeaks = (comparison, sample) => {
  return Math.round((10000 * (sample - comparison)) / comparison) / 100;
};

export const getMonthlyBins = ({
  startDate,
  endDate,
  currentDate,
  bucketMethod,
  shouldAppend = false,
}) => {
  const bins = [];

  if (!startDate || !endDate) {
    return bins;
  }

  const isLeading = bucketMethod === "Leading";
  const startMoment = moment(startDate);
  const endMoment = moment(endDate);
  const currentMoment = moment(currentDate);

  if (!isLeading) {
    startMoment.subtract(1, "month");
    endMoment.subtract(1, "month");
  }

  for (
    startMoment;
    startMoment.isBefore(endMoment);
    startMoment.add(1, "month")
  ) {
    const binFromStartDate = moment(startMoment);

    if (!isLeading) {
      binFromStartDate.add(1, "month");
    }

    bins.push({
      monthIndex: binFromStartDate.month(),
      month: monthShortNameFromIndex(binFromStartDate.month()),
      year: binFromStartDate.year(),
      startDate: moment(startMoment).toDate(),
      endDate: moment(startMoment).add(1, "month").toDate(),
      isCurrentMonth: currentMoment.isBefore(endMoment),
    });
  }

  if (shouldAppend) {
    const binFromEndDate = moment(endMoment);

    if (bucketMethod !== "Leading") {
      binFromEndDate.add(1, "month");
    }

    bins.push({
      monthIndex: binFromEndDate.month(),
      month: monthShortNameFromIndex(binFromEndDate.month()),
      year: binFromEndDate.year(),
      startDate: endMoment.toDate(),
      endDate: currentMoment.toDate(),
      isCurrentMonth: true,
    });
  }

  return bins;
};

export const getPeakDemandFromDatapointIntervals = (
  intervals,
  day,
  start = 8,
  end = 18,
) => {
  const date = moment(day);
  const windowEnd = moment(date).hour(end);
  const windowStart = moment(date).hour(start).subtract(30, "minutes");

  const numberOfIntervals = intervals.length;
  const threshold = numberOfIntervals === 288 ? 6 : 2;

  const peak = {};
  const sample = [];

  for (const interval of intervals) {
    if (sample.length < threshold) {
      sample.push(interval);
    } else {
      if (date.isAfter(windowStart) && date.isBefore(windowEnd)) {
        const samplePeak = arrayAverage(sample);
        const minuteModulus = date.minute() % 15;
        if ((!peak?.peak || samplePeak > peak.peak) && minuteModulus === 0) {
          peak.peak = arrayAverage(sample);
          peak.timestamp = date.toDate();
          peak.sample = [...sample];
        }
      }
      sample.shift();
      sample.push(interval);
    }
    date.add(numberOfIntervals === 288 ? 5 : 15, "minutes");
  }
  return peak;
};

export const calculatePercentChangeForDailyMetricsBins = ({
  bins,
  comparisonYear,
  currentYear,
}) =>
  bins.map((bin) => {
    const { monthIndex, demandValue, year } = bin;
    let yearOverYear;

    if (year === currentYear) {
      const comparisonBin = bins.find(
        (b) => b.monthIndex === monthIndex && b.year === comparisonYear,
      );

      if (!!comparisonBin) {
        yearOverYear = comparePeaks(comparisonBin.demandValue, demandValue);

        return {
          ...bin,
          yearOverYear: !Number.isNaN(yearOverYear) ? yearOverYear : null,
        };
      }
    }
    return { ...bin };
  });

export const mergeDailyMetricsBinsByMonth = (bins) => {
  const result = new Array(12);

  for (const bin of bins) {
    const { monthIndex, month, year, demandValue, demandDate, isCurrentMonth } =
      bin;
    if (!result[monthIndex]) {
      result[monthIndex] = {
        monthIndex,
        month,
        [`year_${year}`]: {
          demandValue,
          demandDate,
        },
        isCurrentMonth,
        currentPeak: isCurrentMonth ? demandValue : null,
        yearOverYear: [bin?.yearOverYear ?? null],
      };
    } else {
      result[monthIndex] = {
        ...result[monthIndex],
        [`year_${year}`]: {
          demandValue,
          demandDate,
        },
        isCurrentMonth,
        currentPeak: isCurrentMonth ? demandValue : null,
        yearOverYear: [
          ...result[monthIndex]["yearOverYear"],
          bin?.yearOverYear ?? null,
        ],
      };
    }
  }

  return result;
};
